import { ArrowLeft, RefreshCw, Sparkles, CheckCircle, Users, Globe, MessageSquare, Briefcase, ChevronRight, Tags, Layout } from 'lucide-react'
import './AICreateCampaign.css'
import '../Pages.css'

const GENERATE_STEPS = [
  'Analysing lead profile & company data...',
  'Crafting personalised email sequence...',
  'Setting schedule based on lead timezone...',
  'Finalising campaign options...'
]

const PERSONA_PRESETS = [
  'CEO / Founder', 'CTO / VP Engineering', 'VP of Sales', 'VP of Marketing',
  'Sales Manager', 'Marketing Manager', 'Head of Growth', 'SDR / BDR',
  'Product Manager', 'Operations Manager', 'CFO / Finance Lead', 'HR / People Ops'
]

const COMPANY_SIZES = [
  { label: '1–10', value: 'startup (1–10 employees)' },
  { label: '11–50', value: 'small business (11–50 employees)' },
  { label: '51–200', value: 'SMB (51–200 employees)' },
  { label: '201–1000', value: 'mid-market (201–1000 employees)' },
  { label: '1000+', value: 'enterprise (1000+ employees)' }
]

const REGIONS = [
  { label: 'US East', timezone: 'EST' },
  { label: 'US Central', timezone: 'CST' },
  { label: 'US West', timezone: 'PST' },
  { label: 'UK / Ireland', timezone: 'GMT' },
  { label: 'Central Europe', timezone: 'CET' },
  { label: 'India', timezone: 'IST' },
  { label: 'Australia East', timezone: 'AEST' },
  { label: 'Japan / Korea', timezone: 'JST' },
  { label: 'Global / Remote', timezone: 'UTC' }
]

const TONES = [
  {
    value: 'professional',
    label: 'Professional',
    desc: 'Formal, polished, business-focused',
    example: '"I wanted to reach out regarding…"'
  },
  {
    value: 'friendly',
    label: 'Friendly',
    desc: 'Warm, conversational, approachable',
    example: '"Hey {{firstName}}, quick one…"'
  },
  {
    value: 'direct',
    label: 'Direct',
    desc: 'Brief, punchy, zero fluff',
    example: '"3 lines. No pitch decks."'
  },
  {
    value: 'educational',
    label: 'Educational',
    desc: 'Value-first, insight-led, consultative',
    example: '"Most {{persona}}s we speak to say…"'
  }
]

const EMAIL_STYLES = [
  {
    value: 'concise',
    label: 'Concise',
    desc: 'Short & punchy — straight to the point',
    example: '50–90 words per email. Best for busy executives.',
    icon: '⚡'
  },
  {
    value: 'detailed',
    label: 'Detailed',
    desc: 'Thorough with depth — good for complex B2B',
    example: '120–180 words. Structured paragraphs with proof points.',
    icon: '📋'
  },
  {
    value: 'newsletter',
    label: 'Newsletter',
    desc: 'Rich content with sections — feels like a valuable read',
    example: '150–250 words. Insights, bullets, mini case studies.',
    icon: '📰'
  },
  {
    value: 'story',
    label: 'Storytelling',
    desc: 'Narrative-driven — emotionally engaging micro-stories',
    example: '120–180 words. Opens with a scenario or anecdote.',
    icon: '📖'
  },
  {
    value: 'casual',
    label: 'Casual',
    desc: 'Slack-message energy — like texting a work friend',
    example: '40–70 words. Ultra-short, lowercase vibe.',
    icon: '💬'
  }
]

export default function LeadProfileInput({ profile, onChange, onBack, onGenerate, generating, generateStep, availableVariables = [] }) {
  const update = (field, value) => onChange({ ...profile, [field]: value })

  return (
    <>
      {/* Generating Overlay */}
      {generating && (
        <div className="ai-generating-overlay">
          <div className="ai-generating-modal">
            <div className="ai-generating-icon">
              <Sparkles size={28} style={{ color: 'white' }} />
            </div>
            <h3>Building Your Campaign</h3>
            <p>AI is crafting emails tailored specifically to your leads...</p>
            <div className="ai-generating-steps">
              {GENERATE_STEPS.map((step, i) => {
                const isDone = i < generateStep
                const isActive = i === generateStep
                return (
                  <div
                    key={i}
                    className={`ai-generating-step${isActive ? ' active' : ''}${isDone ? ' done' : ''}`}
                  >
                    <div className="ai-generating-step-dot" />
                    {isDone && (
                      <CheckCircle size={14} style={{ color: '#059669', marginLeft: '-2px', flexShrink: 0 }} />
                    )}
                    <span>{step}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: 'var(--color-text-primary)' }}>
              Tell us about your leads
            </h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', margin: '0.25rem 0 0' }}>
              This shapes email tone, send timing, and personalisation — the more specific, the less generic
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={onBack} className="btn btn-ghost">
              <ArrowLeft size={16} />
              Back
            </button>
            <button onClick={onGenerate} disabled={generating} className="btn btn-ai">
              {generating
                ? <><RefreshCw size={16} className="animate-spin" /> Generating...</>
                : <><Sparkles size={16} /> Generate Campaign</>
              }
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.75rem', background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: '8px', fontSize: '0.78rem', color: 'var(--color-text-secondary)' }}>
          <Sparkles size={14} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
          <span>Campaign generation uses <strong>5 AI credits</strong> per generation</span>
        </div>

        {/* Card 1: Lead Persona */}
        <div className="card">
          <div className="card-header">
            <h3><Users size={17} /> Who are your leads?</h3>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label>
                Job Title / Role
                <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', marginLeft: '0.4rem' }}>
                  — type or click a preset
                </span>
              </label>
              <input
                type="text"
                value={profile.persona || ''}
                onChange={e => update('persona', e.target.value)}
                placeholder="e.g., VP of Sales, Head of Marketing, CTO…"
              />
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.425rem', marginTop: '0.75rem' }}>
              {PERSONA_PRESETS.map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => update('persona', profile.persona === p ? '' : p)}
                  className={`campaign-filter-chip${profile.persona === p ? ' active' : ''}`}
                  style={{ fontSize: '0.775rem' }}
                >
                  {p}
                </button>
              ))}
            </div>

            <div className="form-row" style={{ marginTop: '1.25rem' }}>
              <div className="form-group">
                <label>Lead Industry</label>
                <input
                  type="text"
                  value={profile.leadIndustry || ''}
                  onChange={e => update('leadIndustry', e.target.value)}
                  placeholder="e.g., SaaS, E-commerce, Fintech, Healthcare…"
                />
              </div>
              <div className="form-group">
                <label>
                  Your Role / What You Sell
                  <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', marginLeft: '0.4rem' }}>(optional)</span>
                </label>
                <input
                  type="text"
                  value={profile.senderRole || ''}
                  onChange={e => update('senderRole', e.target.value)}
                  placeholder="e.g., AE at Acme — selling AI outreach tools to sales teams"
                />
              </div>
            </div>

            <div style={{ marginTop: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '0.625rem' }}>
                Lead Company Size
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {COMPANY_SIZES.map(s => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => update('companySize', profile.companySize === s.value ? '' : s.value)}
                    className={`day-toggle-btn${profile.companySize === s.value ? ' active' : ''}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Lead Location → Schedule Timezone */}
        <div className="card">
          <div className="card-header">
            <h3><Globe size={17} /> Where are your leads located?</h3>
          </div>
          <div className="card-body">
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', margin: '0 0 0.875rem' }}>
              Sets the send-window timezone so emails land during their working hours — not yours.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {REGIONS.map(r => (
                <button
                  key={r.timezone}
                  type="button"
                  onClick={() => update('timezone', profile.timezone === r.timezone ? '' : r.timezone)}
                  className={`campaign-filter-chip${profile.timezone === r.timezone ? ' active' : ''}`}
                >
                  {r.label}
                  <span style={{ opacity: 0.6, marginLeft: '0.25rem', fontSize: '0.72rem' }}>
                    ({r.timezone})
                  </span>
                </button>
              ))}
            </div>
            {profile.timezone && (
              <div style={{
                marginTop: '0.875rem',
                fontSize: '0.8rem',
                color: '#7c3aed',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem'
              }}>
                <ChevronRight size={13} />
                Schedule will default to{' '}
                <strong style={{ marginLeft: '0.2rem' }}>9am–5pm {profile.timezone}, Mon–Fri</strong>
              </div>
            )}
          </div>
        </div>

        {/* Card 3: Email Tone */}
        <div className="card">
          <div className="card-header">
            <h3><MessageSquare size={17} /> Email Tone</h3>
          </div>
          <div className="card-body">
            <div className="ai-tone-grid">
              {TONES.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => update('tone', t.value)}
                  className={`ai-tone-card${profile.tone === t.value ? ' active' : ''}`}
                >
                  <span className="ai-tone-label">{t.label}</span>
                  <span className="ai-tone-desc">{t.desc}</span>
                  <span className="ai-tone-example">{t.example}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Card 4: Email Style */}
        <div className="card">
          <div className="card-header">
            <h3><Layout size={17} /> Email Style</h3>
          </div>
          <div className="card-body">
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', margin: '0 0 0.875rem' }}>
              Choose how your emails look and feel — this controls length, structure, and content depth.
            </p>
            <div className="ai-tone-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
              {EMAIL_STYLES.map(s => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => update('emailStyle', s.value)}
                  className={`ai-tone-card${profile.emailStyle === s.value ? ' active' : ''}`}
                >
                  <span style={{ fontSize: '1.25rem', marginBottom: '0.25rem' }}>{s.icon}</span>
                  <span className="ai-tone-label">{s.label}</span>
                  <span className="ai-tone-desc">{s.desc}</span>
                  <span className="ai-tone-example" style={{ fontSize: '0.72rem', opacity: 0.7 }}>{s.example}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Card 5: Custom Context / Pitch */}
        <div className="card">
          <div className="card-header">
            <h3>
              <Briefcase size={17} /> Your Pitch & Context
              <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--color-text-muted)', marginLeft: '0.5rem' }}>
                optional but powerful
              </span>
            </h3>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label>What problem do you solve? What's your key differentiator or proof point?</label>
              <textarea
                value={profile.customPrompt || ''}
                onChange={e => update('customPrompt', e.target.value)}
                rows={5}
                style={{ resize: 'vertical' }}
                placeholder={`e.g., "Our leads are VPs of Sales frustrated with < 3% reply rates. We help them 3× replies using AI personalisation — integrates with their CRM in 10 min, no dev needed. ROI visible within 2 weeks. Main objection: 'we already have a tool' — counter with our open rate data."`}
              />
              <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', display: 'block', marginTop: '0.4rem', lineHeight: 1.5 }}>
                Mention real numbers, pain points, objections, or outcomes. The more specific you are, the more personalised the emails AI writes.
              </span>
            </div>
          </div>
        </div>

        {/* Card 5: Variables that AI will use */}
        {availableVariables.length > 0 && (
          <div className="card" style={{ borderColor: 'rgba(99, 102, 241, 0.2)', background: 'rgba(99, 102, 241, 0.02)' }}>
            <div className="card-header">
              <h3><Tags size={17} /> Lead Variables for AI</h3>
            </div>
            <div className="card-body">
              <p style={{ fontSize: '0.825rem', color: 'var(--color-text-secondary)', margin: '0 0 0.75rem' }}>
                AI will use these personalisation variables from your leads when generating email content:
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {availableVariables.map((v) => (
                  <span
                    key={v.key}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      padding: '0.3rem 0.65rem',
                      background: v.custom ? 'rgba(245, 158, 11, 0.08)' : 'rgba(99, 102, 241, 0.08)',
                      border: `1px solid ${v.custom ? 'rgba(245, 158, 11, 0.2)' : 'rgba(99, 102, 241, 0.2)'}`,
                      borderRadius: '999px',
                      fontSize: '0.775rem',
                      color: v.custom ? '#d97706' : '#6366f1',
                      fontWeight: 500,
                    }}
                  >
                    <code style={{ fontSize: '0.7rem' }}>{`{{${v.key}}}`}</code>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  )
}
